import { InjectedPropsProvider } from 'Components/Core/InjectedPropsContext';
import { WorksheetBatchUpdateAllTestsTypeId } from 'Components/WorksheetBatchUpdate';
import { getConfigForTest } from 'Components/Worksheets/Config';
import { evaluateConfig, pickConfig } from 'Components/Worksheets/Core/Helpers/configHelpers';
import React, { useCallback } from 'react';

/**
 * A special props provider that injects the label for a given name
 * based solely on the type id of the worksheet (i.e. with no worksheet context).
 *
 * Limitations:
 * * Any labels that vary depending on worksheet state will use the default label
 * * Units are not included when determined by equipment readability properties
 */
let WorksheetLabelByTypeInjectedPropsProvider: React.FC<React.PropsWithChildren<{ typeId: string }>> = ({
  typeId,
  children,
}) => {
  const getProviderProps = useCallback(
    (consumerProps: any, modifiers = []) => {
      let mutableConsumerProps = { ...consumerProps };

      modifiers.forEach((m: any) => m(mutableConsumerProps, {}));

      const config = evaluateConfig(
        pickConfig(typeId !== WorksheetBatchUpdateAllTestsTypeId ? getConfigForTest(typeId) ?? {} : {}, [
          'label',
          'labelUom',
          'uom',
        ]),
        consumerProps.name,
        { name: consumerProps.name, worksheetType: { Id: typeId }, worksheet: {} }
      );

      const label = mutableConsumerProps.label || config?.label;
      const labelUom = mutableConsumerProps.labelUom || config?.labelUom || mutableConsumerProps.uom || config.uom;

      return {
        ...mutableConsumerProps,
        label: label && labelUom && !label.endsWith(` (${labelUom})`) ? `${label} (${labelUom})` : label,
      };
    },
    [typeId]
  );

  return <InjectedPropsProvider getProviderProps={getProviderProps}>{children}</InjectedPropsProvider>;
};

export { WorksheetLabelByTypeInjectedPropsProvider };
export default WorksheetLabelByTypeInjectedPropsProvider;
